import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FloorModel } from "proximiio-js-library/lib/models/floor";
import { useIdle, useMedia } from "react-use";
import bbox from "@turf/bbox";
import centerOfMass from "@turf/center-of-mass";
import { FaCaretLeft, FaCaretRight } from "react-icons/fa6";
import MapView from "@/components/MapView";
import FloorPicker from "@/components/FloorPicker";
import PoiSearch from "@/components/PoiSearch";
import Sidebar from "@/components/Sidebar";
import LocateMeButton from "@/components/LocateMeButton";
import LanguageToggle from "@/components/LanguageToggle";
import ResetViewButton from "@/components/ResetViewButton";
import { Toaster } from "@/components/ui/toaster";
import useMapStore from "@/store/mapStore";
// import LoadingGifNoText from "@/assets/cc-loading.gif";
// import LoadingBG from "@/assets/loading-bg.png";
import { cn } from "./lib/utils";
import DateTime from "./components/DateTime";
import FloorUpAnimation from "@/assets/floors-to-arrow-up.gif";
import FloorDownAnimation from "@/assets/floors-to-arrow-down.gif";
import FloorUpAnimationAR from "@/assets/floor-to-arrow-up-AR.gif";
import FloorDownAnimationAR from "@/assets/floor-to-arrow-down-AR.gif";
import FloorUpAnimationRU from "@/assets/floor-to-arrow-up-RU.gif";
import FloorDownAnimationRU from "@/assets/floor-to-arrow-down-RU.gif";
import FloorUpAnimationZH from "@/assets/floor-to-arrow-up-ZH.gif";
import FloorDownAnimationZH from "@/assets/floor-to-arrow-down-ZH.gif";
import Overlay from "./components/Overlay";
import { AdModel } from "proximiio-js-library/lib/models/ad";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./components/ui/carousel";
import ClassNames from "embla-carousel-class-names";
import Autoplay from "embla-carousel-autoplay";
import { useFetchActiveAds } from "./hooks/useFetchActiveAds";
import useDebugger from "./hooks/useDebugger";
import Feature from "proximiio-js-library/lib/models/feature";
import MoreButton from "./components/MoreButton";
import QRCode from "./components/QRCode";
import { FaTimes } from "react-icons/fa";
import LoadingGif from "@/assets/loading.gif";
import useQueueTimes, { FetchStatus, QueueTime } from "./hooks/useQueueTimes";
import useFeaturesStore from "./store/featuresStore";

function App() {
  const maintenanceMode = false;
  const showLanguageToggle =
    import.meta.env.VITE_WAYFINDING_SHOW_LANGUAGE_TOGGLE === "true";
  const showResetViewButton =
    import.meta.env.VITE_WAYFINDING_SHOW_RESET_BUTTON === "true";
  const showAds = import.meta.env.VITE_WAYFINDING_SHOW_ADS === "true";
  const showDatetime = import.meta.env.VITE_WAYFINDING_SHOW_DATETIME === "true";
  const animationsEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_LEVELCHANGE_ANIMATIONS === "true";
  const findMyCarEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_FIND_MY_CAR === "true";
  const { i18n } = useTranslation();
  const sessionIsIdle = useIdle(
    import.meta.env.VITE_WAYFINDING_SESSION_TIMEOUT,
  );
  const wideScreen = useMedia("(min-width: 1024px)");
  const verticalKioskScreen = useMedia(
    "(orientation: portrait) and (min-width: 1079px)",
  );
  const [queueData, queueFetchStatus] = useQueueTimes();

  const [appInitiated, setAppInitiated] = useMapStore((state) => [
    state.appInitiated,
    state.setAppInitiated,
  ]);
  const [kioskMode, setKioskMode] = useMapStore((state) => [
    state.kioskMode,
    state.setKioskMode,
  ]);

  const map = useMapStore((state) => state.map);
  const mapLoaded = useMapStore((state) => state.mapLoaded);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const activeFilter = useMapStore((state) => state.activeFilter);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const overlayContent = useMapStore((state) => state.overlayContent);
  const isLoading = useMapStore((state) => state.isLoading);
  const currentLang = useMapStore((state) => state.currentLang);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const stepAnimated = useMapStore((state) => state.stepAnimated);
  const currentStep = useMapStore((state) => state.currentStep);
  const navMode = useMapStore((state) => state.navMode);
  const activeAds = useMapStore((state) => state.activeAds);
  const ads = useMapStore((state) => state.ads);
  const stops = useMapStore((state) => state.stops);
  const mapPrepared = useMapStore((state) => state.mapPrepared);
  const dataFetched = useMapStore((state) => state.dataFetched);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const hideControls = useMapStore((state) => state.hideControls);
  const kioskScaling = useMapStore((state) => state.kioskScaling);
  const features = useFeaturesStore((state) => state.features);
  const prevFloorRef = useRef<FloorModel>(currentFloor);
  const prevHaveRouteDetailsRef = useRef<boolean>(haveRouteDetails);

  const setCurrentLang = useMapStore((state) => state.setCurrentLang);
  const setAppSession = useMapStore((state) => state.setAppSession);
  const resetViewStore = useMapStore((state) => state.resetView);
  const locateMe = useMapStore((state) => state.locateMe);
  const setAccessibleRoute = useMapStore((state) => state.setAccessibleRoute);
  const setMobileMode = useMapStore((state) => state.setMobileMode);
  const setVerticalKioskMode = useMapStore(
    (state) => state.setVerticalKioskMode,
  );
  const setActiveAds = useMapStore((state) => state.setActiveAds);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setGpsMode = useMapStore((state) => state.setGpsMode);
  const setGlobalSearchOpen = useMapStore((state) => state.setGlobalSearchOpen);
  const setAllowSave = useMapStore((state) => state.setAllowSave);

  const [animationInProgress, setAnimationInProgress] = useState(false);
  const [animationType, setAnimationType] = useState("");
  const [adDisplayed, setAdDisplayed] = useState(false);
  const [adToggleDouble, setAdToggleDouble] = useState(true);

  const idleTime = routeFinish?.id ? 50e3 : 30e3; // 50/30 seconds to idle time
  const kioskIsIdle = useIdle(idleTime);
  const mapReady = useRef(false);
  const [adWidth, setAdWidth] = useState(0); // State to store ad container width
  const adImageRef = useRef<HTMLImageElement | null>(null); // Ref for the ad container
  const adsTimeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const { fetchActiveAds } = useFetchActiveAds();
  const [throwDebug] = useDebugger();

  // handle reset to default view
  const resetView = useCallback(() => {
    console.log("RESET VIEW");
    resetViewStore();
    locateMe();
    setAccessibleRoute({ accessibleRoute: false, forceAccessibleRoute: false });
    setCurrentLang("en");
    if (Object.keys(map).length > 0) {
      map.refetch();
      map.setFeaturesHighlight([]);
      fetchActiveAds();
    }
  }, [
    map,
    resetViewStore,
    locateMe,
    setAccessibleRoute,
    setCurrentLang,
    fetchActiveAds,
  ]);

  // This effect hook handles URL query parameters related to language and kiosk mode
  useEffect(() => {
    if (appInitiated) {
      return;
    }

    // Extract URL query parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Retrieve 'language' parameter from URL or default to 'en' if not found
    const urlLanguage = urlParams.get("language") ?? "en";

    // Update language state with 'language' parameter from URL
    setCurrentLang(urlLanguage);
    i18n.changeLanguage(urlLanguage);

    const rootHtml = document.getElementById("root-html");
    if (rootHtml) {
      rootHtml.setAttribute("dir", urlLanguage === "ar" ? "rtl" : "ltr");
      rootHtml.setAttribute("data-dir", urlLanguage === "ar" ? "rtl" : "ltr");
    }

    document.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length > 1) {
          event.preventDefault();
        }
      },
      { passive: false },
    );

    document.addEventListener(
      "wheel",
      (event) => {
        const { ctrlKey } = event;
        if (ctrlKey) {
          event.preventDefault();
          return;
        }
        //event.preventDefault();
        //event.stopPropagation();
      },
      { passive: false },
    );

    // If 'kiosk' parameter exists in URL, set kiosk mode
    if (urlParams.get("kiosk")) {
      setKioskMode(true);
      setGpsMode(false);
    }

    if (urlParams.get("content")) {
      const content = urlParams.get("content");
      if (content === "parkAssist" && findMyCarEnabled) {
        setOverlayContent("findMyCar");
      }
      if (content === "route") {
        setGlobalSearchOpen(true);
      }
    }

    if (urlParams.get("allowSave")) {
      const allowSave = urlParams.get("allowSave");
      if (allowSave === "true") {
        setAllowSave(true);
      }
    }

    if (!appInitiated) {
      throwDebug({ message: "Initiating the app" });
    }

    setAppInitiated(true);
  }, [
    appInitiated,
    i18n,
    findMyCarEnabled,
    mobileMode,
    setKioskMode,
    setCurrentLang,
    setAppInitiated,
    setOverlayContent,
    setGlobalSearchOpen,
    throwDebug,
    setGpsMode,
    setAllowSave,
  ]);

  // This effect hook handles kioskMode changes
  useEffect(() => {
    if (kioskMode && kioskIsIdle && !mobileMode) {
      console.log("KIOSK IS IDLE, RESEEEET");
      resetView();
    }
  }, [kioskMode, kioskIsIdle, mobileMode, resetView]);

  // This effect hook handles ads initiation
  useEffect(() => {
    if (mapLoaded) {
      fetchActiveAds();
      setTimeout(() => {
        mapReady.current = true;
      }, 500);
    }
  }, [mapLoaded, fetchActiveAds]);

  // This effect handles ads
  useEffect(() => {
    const resetAdState = () => {
      setActiveAds([] as AdModel[]);
      setAdDisplayed(false);
    };

    if (!showAds) {
      resetAdState();
      return;
    }

    const clearTimeouts = () => {
      // Clear all existing timeouts before setting new ones
      adsTimeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      adsTimeoutRefs.current = []; // Reset the array after clearing the timeouts
    };

    const displayAdsWithDelay = (ads: AdModel[] | null) => {
      if (ads && ads.length > 0) {
        // Clear any existing timeouts before starting new ones
        clearTimeouts();

        ads.sort((a, b) => a.endDate! - b.endDate!);
        ads.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));

        const timeout1 = setTimeout(() => {
          setActiveAds(ads);
          const timeout2 = setTimeout(() => {
            setAdDisplayed(true);
          }, 0);

          // Store the second timeout in the array
          adsTimeoutRefs.current.push(timeout2);
        }, 500);

        // Store the first timeout in the array
        adsTimeoutRefs.current.push(timeout1);
      } else {
        resetAdState();
      }
    };

    if (routeFinish?.id && !activeFilter?.id) {
      const amenityAds = ads.filter((ad) =>
        ad.amenities?.includes(routeFinish.properties.amenity),
      );
      const poiAds = ads.filter((ad) => ad.features?.includes(routeFinish.id));
      let allAds = [...amenityAds, ...poiAds];

      // Remove duplicates based on the `id` property
      allAds = allAds.filter(
        (obj, index, self) => index === self.findIndex((o) => o.id === obj.id),
      );

      displayAdsWithDelay(allAds);
      return;
    }

    if (activeFilter?.id && !routeFinish?.id) {
      const filterId = activeFilter.id;
      const amenityAds = ads.filter((ad) =>
        Array.isArray(filterId)
          ? ad.amenities?.some((amenity) => filterId.includes(amenity))
          : ad.amenities?.includes(filterId),
      );

      displayAdsWithDelay(amenityAds);
      return;
    }

    resetAdState();

    return () => {
      clearTimeouts();
    };
  }, [routeFinish, showAds, ads, activeFilter?.id, setActiveAds]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const toggleAd = (isDouble: boolean) => {
      setAdToggleDouble(isDouble);

      // Set timeout for the next toggle
      timeoutId = setTimeout(
        () => {
          toggleAd(!isDouble); // Toggle the state
        },
        isDouble ? 1000 : 3000,
      ); // 3 seconds for single arrow, 1 second for double arrow
    };

    // Start the loop with single arrow (false)
    toggleAd(false);

    return () => clearTimeout(timeoutId); // Cleanup timeout on unmount
  }, []);

  useEffect(() => {
    // Store the current ref in a variable to avoid stale ref in cleanup
    const adImage = adImageRef.current;

    // Function to update the ad container width
    const updateAdWidth = () => {
      if (adImage) {
        if (mobileMode && adWidth) return;
        setAdWidth(adImage.offsetWidth);
      }
    };

    // Set up a ResizeObserver to track changes
    const resizeObserver = new ResizeObserver(() => updateAdWidth());
    if (adImage) {
      resizeObserver.observe(adImage);
    }

    // Cleanup observer on component unmount
    return () => {
      if (adImage) {
        resizeObserver.unobserve(adImage);
      }
    };
  }, [adDisplayed, mobileMode, adWidth]);

  // This effect hook set app session on init
  useEffect(() => {
    setAppSession();
  }, [setAppSession]);

  // This effect hook set reset app session on idle time
  useEffect(() => {
    if (sessionIsIdle) {
      setAppSession();
    }
  }, [sessionIsIdle, setAppSession]);

  // This effect hook handle level change animations
  const handleAnimationType = useCallback(
    (currentLevel: number, prevLevel: number) => {
      let animation = "";
      if (currentLevel > prevLevel) {
        if (currentLang === "en") animation = FloorUpAnimation;
        if (currentLang === "ar") animation = FloorUpAnimationAR;
        if (currentLang === "ru") animation = FloorUpAnimationRU;
        if (currentLang === "zh") animation = FloorUpAnimationZH;
      } else if (currentLevel < prevLevel) {
        if (currentLang === "en") animation = FloorDownAnimation;
        if (currentLang === "ar") animation = FloorDownAnimationAR;
        if (currentLang === "ru") animation = FloorDownAnimationRU;
        if (currentLang === "zh") animation = FloorDownAnimationZH;
      }
      setAnimationType(animation);
    },
    [currentLang],
  );
  useEffect(() => {
    if (
      haveRouteDetails &&
      prevHaveRouteDetailsRef.current &&
      Object.keys(currentFloor).length > 0 &&
      Object.keys(prevFloorRef.current).length &&
      currentFloor.id !== prevFloorRef.current.id
    ) {
      console.log("Animation", stepAnimated);
      handleAnimationType(currentFloor.level, prevFloorRef.current.level);
      if (stepAnimated.length > 0 && !stepAnimated.includes(currentStep)) {
        setAnimationInProgress(true);
      }
      map.stopRouteAnimation();
      console.log("Animation started");
      setTimeout(() => {
        console.log("Animation finished");
        setAnimationInProgress(false);
        if (
          Object.keys(map).length > 0 &&
          haveRouteDetails &&
          !stepAnimated.includes(currentStep)
        ) {
          map.restartRouteAnimation({ delay: 1000, recenter: false });
        }
      }, 2000);
    }
    prevFloorRef.current = currentFloor;
    prevHaveRouteDetailsRef.current = haveRouteDetails;
  }, [
    haveRouteDetails,
    currentFloor,
    handleAnimationType,
    map,
    stepAnimated,
    currentStep,
  ]);

  useEffect(() => {
    setMobileMode(!wideScreen);
    setVerticalKioskMode(verticalKioskScreen);
  }, [wideScreen, verticalKioskScreen, setMobileMode, setVerticalKioskMode]);

  async function initSentry() {
    const Sentry = await import("@sentry/react");
    if (import.meta.env.MODE !== "development") {
      Sentry.init({
        dsn: "https://848f8ae99e085ad1a9fcfb2f27dea0e2@o4507531539120128.ingest.de.sentry.io/4507531857363025",
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: 0.01, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/yourserver\.io\/api/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      });
    }
  }
  useEffect(() => {
    if (mapLoaded) {
      initSentry();
    }
  }, [mapLoaded]);

  useEffect(() => {
    window.centerMap = (args: {
      bearing?: number;
      pitch?: number;
      level?: number;
      zoom?: number | "bounds";
    }) => {
      const { bearing, pitch, level, zoom = "bounds" } = args;

      if (!mapLoaded) {
        console.log("Map not loaded");
        return;
      }

      const features = map
        .getMapState()
        .allFeatures.features.filter(
          (f: Feature) =>
            f.properties.level ===
              (level !== undefined ? level : currentFloor.level) &&
            f.properties.type === "poi",
        );

      if (features.length === 0) {
        console.log("No features found");
        return;
      }

      if (level !== undefined && level !== currentFloor.level) {
        map.setFloorByLevel(level);
      }

      if (zoom === "bounds") {
        const boundingBox = bbox({ type: "FeatureCollection", features }) as [
          number,
          number,
          number,
          number,
        ];
        map.getMapboxInstance().fitBounds(boundingBox, {
          padding: {
            left: mobileMode ? 50 : currentLang === "ar" ? 100 : 300,
            right: mobileMode ? 50 : currentLang === "ar" ? 300 : 20,
            bottom: mobileMode ? 200 : 130,
          },
          bearing:
            bearing !== undefined
              ? bearing
              : map.getMapboxInstance().getBearing(),
          pitch:
            pitch !== undefined ? pitch : map.getMapboxInstance().getPitch(),
        });
      } else {
        const centerPoint = centerOfMass({
          type: "FeatureCollection",
          features,
        });
        map.getMapboxInstance().jumpTo({
          padding: {
            left: mobileMode ? 50 : currentLang === "ar" ? 100 : 300,
            right: mobileMode ? 50 : currentLang === "ar" ? 300 : 20,
            bottom: mobileMode ? 200 : 130,
          },
          center: centerPoint.geometry.coordinates as [number, number],
          zoom,
          bearing:
            bearing !== undefined
              ? bearing
              : map.getMapboxInstance().getBearing(),
          pitch:
            pitch !== undefined ? pitch : map.getMapboxInstance().getPitch(),
        });
      }
    };

    // optional cleanup
    return () => {
      delete window.centerMap;
    };
  }, [mapLoaded, map, currentFloor, currentLang, mobileMode]);

  // This effect hook set queue data
  useEffect(() => {
    if (
      queueFetchStatus === FetchStatus.Loaded &&
      Object.keys(map).length > 0
    ) {
      for (const queue of queueData as QueueTime[]) {
        if (queue.featureId) {
          const properties = {
            _queue: queue,
          };
          map.updateFeature(
            queue.featureId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            // @ts-ignore
            properties,
            true,
          );
        }
      }
      features.map((f) => {
        const queue = (queueData as QueueTime[]).find(
          (i) => i.featureId === f.id,
        );
        if (queue) {
          f.properties = {
            ...f.properties,
            _queue: queue,
          };
        }
      });
      console.log("queue data", queueData, new Date());
    }
  }, [queueData, queueFetchStatus, map]);

  if (maintenanceMode) {
    return (
      <div className="relative z-100 flex h-screen items-center justify-center bg-white">
        <h1 className="text-center text-3xl font-bold">Under Maintenance</h1>
      </div>
    );
  }

  return (
    <>
      {appInitiated && (
        <main
          className={cn("flex flex-row group-data-[dir=rtl]:flex-row-reverse")}
        >
          <div className="relative flex flex-1">
            <div
              className={cn(
                "relative flex w-full max-w-full transition-[max-width] duration-700 will-change-[max-width]",
                showAds &&
                  activeAds?.length === 0 &&
                  !adDisplayed &&
                  !mobileMode &&
                  !verticalKioskMode &&
                  "transition-none",
              )}
              style={
                showAds &&
                activeAds &&
                activeAds?.length > 0 &&
                adDisplayed &&
                !mobileMode &&
                !verticalKioskMode
                  ? { maxWidth: `calc(100% - ${adWidth - 10}px)` }
                  : {}
              }
            >
              {
                <div
                  className={cn(
                    "absolute flex h-screen w-full overflow-hidden",
                    !mapPrepared && "z-[10]",
                    !mapLoaded && "bg-white",
                  )}
                >
                  <div className="absolute flex h-screen w-full items-center justify-center">
                    <div className="mx-auto">
                      <img
                        src={LoadingGif}
                        alt="Emerald Park"
                        className="mx-auto w-3/4 lg:w-full"
                      />
                    </div>
                  </div>
                </div>
              }
              {isLoading && dataFetched && !animationInProgress && (
                <div className="absolute flex h-screen w-full">
                  <div className="mx-auto">
                    <div className="mx-auto h-40 w-40 animate-spin rounded-full border border-gray-300 border-t-primary" />
                  </div>
                </div>
              )}
              {animationsEnabled && animationInProgress && (
                <div className="absolute z-10 flex h-screen w-full bg-black/70">
                  <div
                    className={cn(
                      "flex h-full w-[90%] flex-col items-center justify-center lg:group-data-[dir=ltr]:ml-96 lg:group-data-[dir=rtl]:mr-96",
                      activeFilter?.id && mobileMode && "h-[48%]",
                      routeFinish?.id && mobileMode && "h-[76%]",
                    )}
                  >
                    <img
                      src={animationType}
                      className="w-full group-data-[dir=rtl]:mr-24 lg:mx-0 lg:w-3/4"
                    />
                  </div>
                </div>
              )}
              {overlayContent && <Overlay content={overlayContent} />}
              {!routeFinish?.id &&
                stops.length === 0 &&
                navMode === "mall" &&
                !hideControls && <PoiSearch />}
              {!hideControls && <Sidebar />}
              {!hideControls && <FloorPicker />}
              {!hideControls && <LocateMeButton />}
              {showLanguageToggle && !mobileMode && <LanguageToggle />}
              {verticalKioskMode && !hideControls && <MoreButton />}
              {showDatetime && <DateTime />}
              {showResetViewButton && <ResetViewButton />}
              {verticalKioskMode && (
                <QRCode
                  size={180}
                  className="fixed top-28 z-10 group-data-[dir=ltr]:left-10 group-data-[dir=rtl]:right-10"
                  type="base"
                />
              )}
              <MapView />
              <Toaster />
            </div>
            {showAds && activeAds && activeAds?.length > 0 && (
              <div
                //ref={adContainerRef}
                className={cn(
                  "absolute z-[101] flex h-screen w-screen translate-x-0 items-center justify-center rounded-none bg-background/80 p-10 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] backdrop-blur-sm transition-all duration-700 group-data-[dir=ltr]:right-0 group-data-[dir=rtl]:left-0 lg:z-100 lg:w-auto lg:max-w-[50%] lg:bg-white lg:p-0 lg:backdrop-blur-0 group-data-[dir=ltr]:lg:rounded-bl-2xl group-data-[dir=ltr]:lg:rounded-tl-2xl group-data-[dir=rtl]:lg:rounded-br-2xl group-data-[dir=rtl]:lg:rounded-tr-2xl",
                  !adDisplayed && "absolute z-[101]",
                  !adDisplayed &&
                    !mobileMode &&
                    "p-0 group-data-[dir=ltr]:translate-x-full group-data-[dir=rtl]:-translate-x-full",
                  !adDisplayed &&
                    mobileMode &&
                    "justify-end group-data-[dir=ltr]:-translate-x-full group-data-[dir=rtl]:translate-x-full group-data-[dir=ltr]:pr-0 group-data-[dir=rtl]:pl-0",
                  verticalKioskMode &&
                    "kiosk:w-full kiosk:max-w-full kiosk:bg-black/80 kiosk:backdrop-blur-sm",
                  !kioskScaling && "origin-top-left scale-150",
                )}
              >
                <div
                  className={cn(
                    "h-full lg:relative kiosk:relative kiosk:h-auto",
                  )}
                >
                  <div
                    className={cn(
                      // Base Styles
                      "absolute bottom-14 flex h-28 w-8 cursor-pointer items-center justify-center transition-all ease-in-out hover:w-12",
                      "rounded-bl-full rounded-tl-full bg-primary text-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",

                      // RTL Rotation
                      "group-data-[dir=rtl]:rotate-180",

                      // LTR/RTL Positioning Based on Ad Display
                      adDisplayed
                        ? "justify-center group-data-[dir=ltr]:-left-8 group-data-[dir=rtl]:-right-8 group-data-[dir=ltr]:hover:-left-12 group-data-[dir=rtl]:hover:-right-12"
                        : "group-data-[dir=ltr]:-left-8 group-data-[dir=rtl]:-right-8 group-data-[dir=rtl]:justify-start group-data-[dir=ltr]:justify-end group-data-[dir=ltr]:hover:-left-12 group-data-[dir=rtl]:hover:-right-12",

                      // Expanded width on specific conditions
                      !adDisplayed &&
                        adToggleDouble &&
                        !mobileMode &&
                        "w-10 group-data-[dir=ltr]:-left-10 group-data-[dir=rtl]:-right-10",

                      // Kiosk Mode Styles
                      kioskMode && [
                        "h-40 w-10 hover:w-14",
                        "group-data-[dir=ltr]:-left-10 group-data-[dir=rtl]:-right-10 group-data-[dir=ltr]:hover:-left-14 group-data-[dir=rtl]:hover:-right-14",
                        adDisplayed
                          ? "rounded-none rounded-bl-full rounded-tl-full"
                          : "",
                        !adDisplayed &&
                          adToggleDouble &&
                          "w-12 group-data-[dir=ltr]:-left-12 group-data-[dir=rtl]:-right-12",
                      ],

                      // Mobile Mode Styles
                      mobileMode && [
                        "bottom-auto top-20 h-20 w-6 hover:w-6",
                        "group-data-[dir=ltr]:left-auto group-data-[dir=ltr]:right-0 group-data-[dir=rtl]:left-0 group-data-[dir=rtl]:right-auto",
                        "group-data-[dir=ltr]:hover:left-auto group-data-[dir=ltr]:hover:right-0 group-data-[dir=rtl]:hover:left-0 group-data-[dir=rtl]:hover:right-auto",
                        !adDisplayed && [
                          "group-data-[dir=ltr]:-right-6 group-data-[dir=ltr]:left-auto group-data-[dir=rtl]:-left-6 group-data-[dir=rtl]:right-auto group-data-[dir=ltr]:hover:-right-6 group-data-[dir=rtl]:hover:-left-6",
                          "group-data-[dir=ltr]:justify-center group-data-[dir=rtl]:justify-center",
                          "rounded-none rounded-br-full rounded-tr-full",
                        ],
                        !adDisplayed &&
                          adToggleDouble &&
                          "w-8 hover:w-8 group-data-[dir=ltr]:-right-8 group-data-[dir=rtl]:-left-8 group-data-[dir=ltr]:hover:-right-8 group-data-[dir=rtl]:hover:-left-8",
                      ],

                      // Vertical Kiosk Mode Styles
                      verticalKioskMode && [
                        adDisplayed &&
                          "!left-[calc(50%-56px)] -top-28 bottom-auto h-20 w-20 rounded-full border-2 border-white bg-transparent shadow-none hover:w-20",
                        !adDisplayed && [
                          "fixed bottom-14 top-auto h-40 w-10 hover:w-14",
                          "group-data-[dir=ltr]:-left-10 group-data-[dir=rtl]:-right-10 group-data-[dir=ltr]:hover:-left-14 group-data-[dir=rtl]:hover:-right-14",
                        ],
                        !adDisplayed &&
                          adToggleDouble &&
                          "w-12 group-data-[dir=ltr]:-left-12 group-data-[dir=rtl]:-right-12",
                      ],
                    )}
                    onClick={() => {
                      setAdDisplayed(!adDisplayed);
                      setAdToggleDouble(false);
                    }}
                  >
                    {adDisplayed &&
                      (mobileMode ? (
                        <FaCaretLeft className="text-xl" />
                      ) : verticalKioskMode ? (
                        <FaTimes className="text-3xl" />
                      ) : (
                        <FaCaretRight className="text-3xl" />
                      ))}
                    {!adDisplayed && (
                      <div className="flex">
                        {adToggleDouble &&
                          (mobileMode ? (
                            <FaCaretRight className="absolute right-3 text-xl opacity-50" />
                          ) : (
                            <FaCaretLeft className="absolute right-2 text-3xl opacity-50" />
                          ))}
                        {mobileMode ? (
                          <FaCaretRight className="text-xl" />
                        ) : (
                          <FaCaretLeft className="text-3xl" />
                        )}
                      </div>
                    )}
                  </div>
                  {
                    <Carousel
                      opts={{
                        loop: true,
                        startIndex: 0,
                        direction: currentLang === "ar" ? "rtl" : "ltr",
                      }}
                      plugins={[
                        ClassNames({}),
                        Autoplay({
                          delay: 3000,
                          stopOnInteraction: true,
                        }),
                      ]}
                      className={cn(
                        "relative rounded-none lg:h-screen lg:overflow-hidden group-data-[dir=ltr]:lg:rounded-bl-2xl group-data-[dir=ltr]:lg:rounded-tl-2xl group-data-[dir=rtl]:lg:rounded-br-2xl group-data-[dir=rtl]:lg:rounded-tr-2xl kiosk:h-auto kiosk:overflow-visible",
                      )}
                      style={adWidth > 0 ? { maxWidth: `${adWidth}px` } : {}}
                    >
                      <CarouselContent className="m-0">
                        {activeAds.map((ad, index) => {
                          return (
                            <CarouselItem key={index} className="p-2.5 lg:p-0">
                              <img
                                src={ad.url}
                                alt={ad.name}
                                ref={adImageRef}
                                className="h-auto max-h-full rounded-xl object-contain shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] lg:h-full lg:rounded-none lg:shadow-none"
                              />
                            </CarouselItem>
                          );
                        })}
                      </CarouselContent>
                      <CarouselPrevious className="-bottom-12 left-[calc(50%-2.5rem)] border-primary text-primary hover:border-primary/60 hover:text-primary/60 lg:bottom-4 lg:left-4 lg:h-12 lg:w-12 kiosk:-bottom-28 kiosk:left-[calc(50%-5rem)] kiosk:top-auto" />
                      <CarouselNext className="-bottom-12 right-[calc(50%-2.5rem)] border-primary text-primary hover:border-primary/60 hover:text-primary/60 lg:bottom-4 lg:right-4 lg:h-12 lg:w-12 kiosk:-bottom-28 kiosk:right-[calc(50%-5rem)] kiosk:top-auto" />
                    </Carousel>
                  }
                </div>
              </div>
            )}
          </div>
        </main>
      )}
    </>
  );
}

export default App;
